import { Box, Button, Checkbox } from '@ouster/webviz-react-components';

import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { AdminDashboardOrgBrief } from './types';
import { useSupportedRegions } from './useAdminDashboard';
import { useStoreActions } from '../../reducers';

type AdminStoragePreferenceConfigProps = {
  handleClose: () => void;
} & Pick<AdminDashboardOrgBrief, 'organization_id' | 'storage_preferences'>;

export default function AdminStoragePreferenceConfig({
  organization_id,
  storage_preferences,
  handleClose,
}: AdminStoragePreferenceConfigProps) {
  const { data: supportedRegions = [], isLoading } = useSupportedRegions();

  const [selectedRegions, setSelectedRegions] = useState<
    Record<string, boolean>
  >({});
  const [isUpdating, setIsUpdating] = useState(false);
  const queryClient = useQueryClient();

  const { updateStoragePreferences } = useStoreActions(
    (actions) => actions.adminDashboard
  );

  // Initialize selected regions based on current storage_preferences
  useEffect(() => {
    if (supportedRegions.length > 0) {
      const regionMap = supportedRegions.reduce((acc, region) => {
        acc[region] = storage_preferences.includes(region);
        return acc;
      }, {} as Record<string, boolean>);
      setSelectedRegions(regionMap);
    }
  }, [supportedRegions, storage_preferences]);

  const handleRegionChange = (region: string, isChecked: boolean) => {
    setSelectedRegions((prev) => ({
      ...prev,
      [region]: isChecked,
    }));
  };

  const handleStoragePreferenceChange = async (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();

    const enabledRegions = Object.entries(selectedRegions)
      .filter(([, isEnabled]) => isEnabled)
      .map(([region]) => region);

    // Don't proceed if no regions are selected
    if (enabledRegions.length === 0) {
      return;
    }

    try {
      setIsUpdating(true);
      await updateStoragePreferences({
        regions: enabledRegions,
        organization_id,
      });
      queryClient.invalidateQueries({
        queryKey: ['adminDashboardSummary'],
        refetchType: 'all',
      });

      handleClose();
    } catch (err) {
      console.error(err);
    } finally {
      setIsUpdating(false);
    }
  };

  const isAtLeastOneRegionSelected = Object.values(selectedRegions).some(
    (isSelected) => isSelected
  );

  if (isLoading) {
    return null;
  }

  return (
    <Box
      as="form"
      display="grid"
      rowGap={6}
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onSubmit={handleStoragePreferenceChange}
      maxW={640}
      data-testid="admin-storage-preference-config-form"
    >
      <Box as="p" fontSize="md" lineHeight={1.6}>
        Select the regions where you want to replicate your data. Enabling
        replication can improve data availability and access speed for users in
        different geographic regions.
      </Box>
      <Box display="grid" rowGap={3}>
        <Box as="p" fontSize="sm" fontWeight={500}>
          Available Regions:
        </Box>
        {supportedRegions.map((region) => (
          <Checkbox
            key={region}
            isChecked={selectedRegions[region] || false}
            onChange={(e) => handleRegionChange(region, e.target.checked)}
            label={region}
          />
        ))}
      </Box>
      <Box display="flex" gap={2} justifyContent="flex-end" alignItems="center">
        {!isAtLeastOneRegionSelected && (
          <Box as="p" color="yellow.400" fontSize={14}>
            At least one region must be selected.
          </Box>
        )}
        <Button
          minWidth={150}
          justifySelf="end"
          type="submit"
          isDisabled={isUpdating || !isAtLeastOneRegionSelected}
          data-testid="admin-storage-preference-config-save"
        >
          Save
        </Button>
      </Box>
    </Box>
  );
}
